import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Loader2, Pencil } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { cn } from '@/lib/utils';
import { fetchUstawieniaWyspy, zapiszUstawienie, type UstawienieWyspy } from '@/lib/ustawienia-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  USTAWIENIA AUTOMATÓW CAŁEJ WYSPY — jedno ustawienie w wierszu, automaty w kolumnach.
 *
 *  ⚠️⚠️ ZAMÓWIONE 23.09.2026: „menu, gdzie można za jednym razem zmieniać wszystkie automaty, które
 *  mają tę samą opcję". Zakładka na karcie automatu patrzy z jednego automatu; tu widać od razu,
 *  który automat odstaje, i zmienia się wybrane albo wszystkie jednym zapisem.
 *
 *  ⚠️ ODSTAJĄCA WARTOŚĆ = inna niż NAJCZĘSTSZA na wyspie, a nie „inna niż na pierwszym automacie".
 *  Na IKEA dwanaście automatów ma identyczne ustawienia poza logo — przy porównaniu z pierwszym
 *  jeden odstający pierwszy automat pomalowałby na żółto jedenaście zdrowych.
 *
 *  ⚠️ PUSTA KOMÓRKA („·") TO BRAK PARAMETRU NA TYM AUTOMACIE, nie pusta wartość — i taki automat
 *  nie jest do wyboru przy zapisie. Pusta wartość pokazuje się jako „—", jak na karcie automatu.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function UstawieniaWyspy() {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const toast = useToast();
  const qc = useQueryClient();
  const [szukaj, setSzukaj] = useState('');
  const [grupa, setGrupa] = useState('');
  const [tylkoRozne, setTylkoRozne] = useState(false);
  const [edycja, setEdycja] = useState<{ param: string; wartosc: string; vmids: number[] } | null>(null);
  const [zapisuje, setZapisuje] = useState(false);

  const dane = useQuery({
    queryKey: ['ustawienia-wyspy', groupId],
    queryFn: () => fetchUstawieniaWyspy(groupId!),
    enabled: groupId !== null,
  });

  const ustawienia = dane.data?.ustawienia ?? [];
  const automaty = dane.data?.automaty ?? [];

  /** Najczęstsza wartość każdego ustawienia — od niej liczy się „odstaje". */
  const najczestsza = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const u of ustawienia) {
      const ile = new Map<string, number>();
      for (const w of u.wartosci) ile.set(w.wartosc ?? '', (ile.get(w.wartosc ?? '') ?? 0) + 1);
      let naj: string | null = null;
      let max = -1;
      for (const [w, n] of ile) {
        if (n > max) {
          naj = w;
          max = n;
        }
      }
      m.set(u.param, naj);
    }
    return m;
  }, [ustawienia]);

  const rozne = (u: UstawienieWyspy): boolean => new Set(u.wartosci.map((w) => w.wartosc ?? '')).size > 1;

  const grupy = useMemo(
    () => [...new Set(ustawienia.map((u) => u.grupa).filter((g): g is string => g !== null))].sort((a, b) => a.localeCompare(b, 'pl')),
    [ustawienia],
  );
  const widoczne = useMemo(() => {
    const q = szukaj.trim().toLowerCase();
    return ustawienia.filter(
      (u) =>
        (grupa === '' || (grupa === '-' ? u.grupa === null : u.grupa === grupa)) &&
        (!tylkoRozne || rozne(u)) &&
        (q === '' ||
          [u.param, u.opis ?? '', ...u.wartosci.map((w) => w.wartosc ?? '')].some((x) => x.toLowerCase().includes(q))),
    );
  }, [ustawienia, szukaj, grupa, tylkoRozne]);

  const pokaz = (u: UstawienieWyspy, w: string | null): string => {
    if (w === null || w === '') return '—';
    if (u.typ === 'B') return w === '1' ? t('ust.tak') : w === '0' ? t('ust.nie') : w;
    return w;
  };

  const otworz = (u: UstawienieWyspy) =>
    setEdycja({
      param: u.param,
      wartosc: najczestsza.get(u.param) ?? (u.typ === 'B' ? '0' : ''),
      vmids: u.wartosci.map((w) => w.vmid),
    });

  const zapisz = async (u: UstawienieWyspy): Promise<void> => {
    if (edycja === null) return;
    const zParametrem = u.wartosci.map((w) => w.vmid);
    /** ⚠️ Wszystkie zaznaczone = „wszystkie" po stronie serwera — ten sam zapis co na karcie automatu. */
    const wszystkie = u.naWszystkich || zParametrem.every((v) => edycja.vmids.includes(v));
    setZapisuje(true);
    try {
      const w = await zapiszUstawienie(
        groupId!,
        u.param,
        edycja.wartosc,
        wszystkie ? { wszystkie: true } : { vmids: edycja.vmids },
      );
      toast.success(t('ust.zakolejkowano').replace('{param}', u.param).replace('{n}', String(w.automatow)));
      setEdycja(null);
      await qc.invalidateQueries({ queryKey: ['ustawienia-wyspy', groupId] });
      await qc.invalidateQueries({ queryKey: ['ustawienia', groupId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setZapisuje(false);
    }
  };

  if (groupId === null) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;
  if (dane.isPending) return <Loader2 className="size-4 animate-spin text-muted-foreground" />;
  if (dane.isError) return <p className="text-sm text-[var(--destructive)]">{(dane.error as Error).message}</p>;

  const zapis = dane.data.zapis;

  return (
    <div className="space-y-3">
      {/* ⚠️ Bez własnego tytułu — to zakładka strony Automaty, nazwę niesie pasek zakładek. */}
      <p className="text-xs text-muted-foreground">
        {t('ustW.licznik').replace('{n}', String(ustawienia.length)).replace('{automatow}', String(automaty.length))}
      </p>

      {!zapis.mozliwy && (
        <p className="text-xs text-[var(--warning)]">
          {t('ust.zapisBlokuje').replace('{wersja}', zapis.wymagana).replace('{wezly}', zapis.starsze.join(', '))}
        </p>
      )}

      {ustawienia.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('ust.brak').replace('{wersja}', zapis.wymagana)}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="h-8 w-72" placeholder={t('ust.szukaj')} value={szukaj} onChange={(e) => setSzukaj(e.target.value)} />
            <Select className="h-8 w-56" value={grupa} onChange={(e) => setGrupa(e.target.value)}>
              <option value="">{t('ust.wszystkieGrupy')}</option>
              {grupy.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
              <option value="-">{t('ust.bezGrupy')}</option>
            </Select>
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={tylkoRozne} onChange={(e) => setTylkoRozne(e.target.checked)} />
              {t('ustW.tylkoRozne')}
            </label>
            <span className="text-xs text-muted-foreground">{widoczne.length}</span>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="sticky left-0 z-10 min-w-72 bg-card px-2 py-1.5 font-medium">{t('ustW.ustawienie')}</th>
                  {automaty.map((a) => (
                    <th key={a.vmid} className="px-2 py-1.5 text-center font-mono font-medium" title={a.nazwa}>
                      {a.vmid}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {widoczne.map((u) => {
                  const naj = najczestsza.get(u.param) ?? null;
                  const edytowany = edycja?.param === u.param;
                  return (
                    <Fragment key={u.param}>
                      <tr className="border-t border-border align-top">
                        <td className="sticky left-0 z-10 bg-card px-2 py-1.5">
                          <div className="flex items-start gap-1">
                            <div className="min-w-0 flex-1">
                              <div>{u.opis ?? u.param}</div>
                              <div className="font-mono text-[10px] text-muted-foreground">
                                {u.param}
                                {u.grupa !== null && <span className="ml-2 font-sans">{u.grupa}</span>}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-6"
                              title={t('ust.zmien')}
                              aria-label={`${t('ust.zmien')} ${u.param}`}
                              disabled={!zapis.mozliwy}
                              onClick={() => (edytowany ? setEdycja(null) : otworz(u))}
                            >
                              <Pencil className="size-3" />
                            </Button>
                          </div>
                        </td>
                        {automaty.map((a) => {
                          const w = u.wartosci.find((x) => x.vmid === a.vmid);
                          if (w === undefined) {
                            return (
                              <td key={a.vmid} className="px-2 py-1.5 text-center text-muted-foreground" title={t('ustW.brakParametru')}>
                                ·
                              </td>
                            );
                          }
                          const odstaje = (w.wartosc ?? '') !== (naj ?? '');
                          return (
                            <td
                              key={a.vmid}
                              className={cn(
                                'max-w-40 truncate px-2 py-1.5 text-center',
                                (u.typ === 'D' || u.typ === 'F') && 'font-mono',
                                odstaje && 'bg-[var(--warning)]/10 font-medium',
                              )}
                              title={w.wartosc ?? ''}
                            >
                              {w.wKolejce && <Clock className="mr-0.5 inline size-3 text-[var(--warning)]" />}
                              {pokaz(u, w.wartosc)}
                            </td>
                          );
                        })}
                      </tr>
                      {edytowany && (
                        <tr className="border-t border-border bg-muted/20">
                          <td colSpan={automaty.length + 1} className="px-2 py-2">
                            <div className="flex flex-wrap items-end gap-3">
                              <label className="grid gap-1">
                                <span className="text-muted-foreground">{t('ustW.nowaWartosc')}</span>
                                {u.typ === 'B' ? (
                                  <Select
                                    className="h-7 w-32"
                                    value={edycja.wartosc}
                                    onChange={(e) => setEdycja({ ...edycja, wartosc: e.target.value })}
                                  >
                                    <option value="1">{t('ust.tak')}</option>
                                    <option value="0">{t('ust.nie')}</option>
                                  </Select>
                                ) : (
                                  <Input
                                    className="h-7 w-72 font-mono text-xs"
                                    inputMode={u.typ === 'I' ? 'numeric' : undefined}
                                    maxLength={255}
                                    value={edycja.wartosc}
                                    onChange={(e) => setEdycja({ ...edycja, wartosc: e.target.value })}
                                  />
                                )}
                              </label>
                              {/*
                                ⚠️ DO WYBORU TYLKO AUTOMATY, KTÓRE TEN PARAMETR MAJĄ. Parametr wspólny
                                (jak `SERIALNUMBERS_MUST_BE_UNIQUE`) idzie zawsze na wszystkie — bez wyboru,
                                tak jak w SPSConfig.
                              */}
                              <div className="grid gap-1">
                                <span className="text-muted-foreground">
                                  {u.naWszystkich ? t('ust.naWszystkich') : t('ustW.naKtorych')}
                                </span>
                                <div className="flex flex-wrap items-center gap-2">
                                  {u.wartosci.map((w) => (
                                    <label key={w.vmid} className="flex items-center gap-1 font-mono">
                                      <input
                                        type="checkbox"
                                        disabled={u.naWszystkich}
                                        checked={u.naWszystkich || edycja.vmids.includes(w.vmid)}
                                        onChange={(e) =>
                                          setEdycja({
                                            ...edycja,
                                            vmids: e.target.checked
                                              ? [...edycja.vmids, w.vmid]
                                              : edycja.vmids.filter((v) => v !== w.vmid),
                                          })
                                        }
                                      />
                                      {w.vmid}
                                    </label>
                                  ))}
                                  {!u.naWszystkich && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-1 text-[11px]"
                                        onClick={() => setEdycja({ ...edycja, vmids: u.wartosci.map((w) => w.vmid) })}
                                      >
                                        {t('ustW.wszystkie')}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-1 text-[11px]"
                                        onClick={() =>
                                          setEdycja({
                                            ...edycja,
                                            vmids: u.wartosci.filter((w) => (w.wartosc ?? '') !== (naj ?? '')).map((w) => w.vmid),
                                          })
                                        }
                                      >
                                        {t('ustW.odstajace')}
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  className="h-7"
                                  disabled={zapisuje || (!u.naWszystkich && edycja.vmids.length === 0)}
                                  onClick={() => void zapisz(u)}
                                >
                                  {zapisuje && <Loader2 className="size-3 animate-spin" />}
                                  {t('common.save')}
                                </Button>
                                <Button size="sm" variant="outline" className="h-7" disabled={zapisuje} onClick={() => setEdycja(null)}>
                                  {t('common.cancel')}
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
